import React, { useState } from "react";
import { SonicApplicationData } from "../../../../common/classes/SonicApplication";
import Checkbox from "../../../../common/forms/Checkbox";
import FormInput from "../../../../common/forms/FormInput";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import ActionBtn from "../../../../common/management/ActionBtn";
import Modal from "../../../../common/management/Modal";
import { CategoryState } from "../../../../utils/CategoryState";
import { timestampToTime } from "../../../../utils/GeneralUtils";
import { OfferStatus } from "../../../../utils/OfferStatus";
import styles from "./EditableApplications.module.scss";

type Props = {
  completedApplications: Array<SonicApplicationData>;
  waitingApplications: Array<SonicApplicationData>;
  underGoingApplications: Array<SonicApplicationData>;
  cancelledApplications: Array<SonicApplicationData>;
  handleStatus: (value: File | string | undefined) => void;
  checkHandler: (app: SonicApplicationData) => void;
  uncheckHandler: (app: SonicApplicationData) => void;
  handleUpdate: (cb: () => void) => void;
  mailCopyHandler: () => void;
  resetHandler: () => void;
  updateApplications: Array<SonicApplicationData>;
  status?: OfferStatus;
  sendMail: boolean;
  handleSendMail: (isChecked: boolean) => void;
  isSelect: boolean;
};
const EditableApplications: React.FC<Props> = ({
  completedApplications,
  waitingApplications,
  underGoingApplications,
  cancelledApplications,
  handleStatus,
  checkHandler,
  uncheckHandler,
  handleUpdate,
  mailCopyHandler,
  resetHandler,
  updateApplications,
  status,
  sendMail,
  handleSendMail,
  isSelect,
}) => {
  const enum EditState {
    None = "None",
    Status = "Status",
    Complete = "Complete",
    Copied = "Copied",
    Confirmation = "Confirmation",
    Error = "Error",
    Loading = "Loading",
  }

  const [editState, setEditState] = useState<EditState>(EditState.None);

  const genModalHeader = () => {
    switch (editState) {
      case EditState.Status:
        return "ステータスの変更";
      case EditState.Confirmation:
        return "確認";
      case EditState.Complete:
      case EditState.Copied:
        return "完了";
      default:
        return "";
    }
  };

  const genModalBody = () => {
    switch (editState) {
      case EditState.Status:
        return (
          <div className={styles["radio"]}>
            <FormInput
              type="radio"
              name="sonic_status"
              label={"ステータスを選択してください"}
              label_class={styles["radio-title"]}
              handler={handleStatus}
              options={[
                [OfferStatus.Complete, "完了済み"],
                [OfferStatus.WaitingForInspection, "対応待ち"],
                [OfferStatus.UndergoingInspection, "対応中"],
                [OfferStatus.Cancelled, "キャンセル"],
              ]}
              initial_value={status}
            />
          </div>
        );
      case EditState.Loading:
        return <LoadingSpinner space={1} admin_page={true} />;
      case EditState.Confirmation:
        return (
          <Checkbox
            id="send-mail-check"
            name="send-mail-checkbox"
            checkHandler={() => {
              handleSendMail(true);
            }}
            uncheckHandler={() => {
              handleSendMail(false);
            }}
            checked={sendMail}
            text="ユーザーにメールを送信する"
          />
        );
      case EditState.Complete:
        return "ステータスの変更が完了しました。";
      case EditState.Copied:
        return "メールアドレスをクリップボードにコピーしました。";
      case EditState.Error:
        return "応募者を選択してください。";
      default:
        return "";
    }
  };

  const genModalHandler = () => {
    switch (editState) {
      case EditState.Status:
        if (
          status === OfferStatus.Complete ||
          status === OfferStatus.Cancelled
        ) {
          return setEditState(EditState.Confirmation);
        } else {
          setEditState(EditState.Loading);
          handleUpdate(() => {
            setEditState(EditState.Complete);
            window.scrollTo({ top: 0, behavior: "smooth" });
          });
          return;
        }
      case EditState.Confirmation:
        setEditState(EditState.Loading);
        handleUpdate(() => {
          setEditState(EditState.Complete);
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
        return;
      default:
        break;
    }
  };

  const genModalButtonText = () => {
    switch (editState) {
      case EditState.Status:
        return "次へ";
      case EditState.Confirmation:
        return "ステータスを変更";
      default:
        return undefined;
    }
  };

  const genGridField = (applications: Array<SonicApplicationData>) => {
    return (
      <div className={styles["grid"]}>
        <div></div>
        <div>応募者名</div>
        <div>メールアドレス</div>
        <div>申請日時</div>
        <div>アクション</div>
        {applications.map((app, i) => (
          <React.Fragment key={i}>
            <Checkbox
              id={`${app.status}${i}`}
              name={`${app.status}-checkbox`}
              checkHandler={() => checkHandler(app)}
              uncheckHandler={() => uncheckHandler(app)}
              checked={updateApplications.some(
                (update) => update.id === app.id
              )}
            />
            <div>
              <span className={styles["ellipsis-text"]}>
                {app.applicant_name}
              </span>
            </div>
            <div>
              <a
                href={`mailto:${app.applicant_email}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles["ellipsis-text"]}
              >
                {app.applicant_email}
              </a>
            </div>
            <div>
              <span className={styles["ellipsis-text"]}>
                {app.timestamp ? timestampToTime(app.timestamp, true) : null}
              </span>
            </div>
            <div>
              <ActionBtn
                label="詳細"
                icon={["fal", "edit"]}
                link={
                  isSelect
                    ? `/admin/project/application/${app.id}`
                    : `/jikei/project/application/${app.id}`
                }
              />
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className={styles["editable-applications"]}>
      <div className={styles["editable-applications__title"]}>応募一覧</div>
      {!completedApplications.length &&
      !waitingApplications.length &&
      !underGoingApplications.length &&
      !cancelledApplications.length ? (
        <LoadingSpinner space={1} admin_page={true} />
      ) : (
        <>
          <div className={styles["editable-applications__btnField"]}>
            <ActionBtn
              label="リセット"
              icon={["fal", "arrows-rotate"]}
              onClick={() => resetHandler()}
            />
          </div>
          <>
            {completedApplications.length > 0 ? (
              <>
                <div
                  className={`${styles["editable-applications__sub-title"]} ${styles["complete"]}`}
                >
                  完了済み
                </div>
                {genGridField(completedApplications)}
              </>
            ) : null}

            {waitingApplications.length > 0 ? (
              <>
                <div
                  className={`${styles["editable-applications__sub-title"]} ${styles["waiting"]}`}
                >
                  対応待ち
                </div>
                {genGridField(waitingApplications)}
              </>
            ) : null}

            {underGoingApplications.length > 0 ? (
              <>
                <div
                  className={`${styles["editable-applications__sub-title"]} ${styles["under-going"]}`}
                >
                  対応中
                </div>
                {genGridField(underGoingApplications)}
              </>
            ) : null}

            {cancelledApplications.length > 0 ? (
              <>
                <div
                  className={`${styles["editable-applications__sub-title"]} ${styles["cancelled"]}`}
                >
                  キャンセル
                </div>
                {genGridField(cancelledApplications)}
              </>
            ) : null}
            <div
              className={`${styles["editable-applications__btnField"]} ${styles["submit"]}`}
            >
              <ActionBtn
                label="メールアドレスをコピー"
                onClick={
                  updateApplications.length > 0
                    ? () => {
                        mailCopyHandler();
                        setEditState(EditState.Copied);
                      }
                    : () => {
                        setEditState(EditState.Error);
                      }
                }
                colorReversal={true}
              />
              <ActionBtn
                label="ステータスを変更"
                onClick={
                  updateApplications.length > 0
                    ? () => setEditState(EditState.Status)
                    : () => setEditState(EditState.Error)
                }
                colorReversal={true}
              />
            </div>
          </>
        </>
      )}

      {editState !== EditState.None ? (
        <Modal
          closeModal={() => {
            setEditState(EditState.None);
            handleStatus(undefined);
            handleSendMail(true);
          }}
          handler={genModalHandler}
          categoryState={CategoryState.None}
          headText={genModalHeader()}
          buttonText={genModalButtonText()}
        >
          {genModalBody()}
        </Modal>
      ) : null}
    </div>
  );
};

export default EditableApplications;
